import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Autocomplete, TextField, Checkbox,
  Box, Paper, Divider, IconButton
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WorkIcon from "@mui/icons-material/Work";
import axios from "axios";
import { toast } from "react-toastify";
import dayjs from 'dayjs';

const CopyPopup = ({ open, onClose, monthData, shiftData, Authorization, propId, YearNm, MonthNm, shiftId }) => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [monthError, setMonthError] = useState(false);
  const [shiftError, setShiftError] = useState(false);
  const userId = sessionStorage.getItem('UserId');
  console.log('shiftId', shiftId)
  const handleMonthChange = (_, newValue) => {
    setSelectedMonth(newValue);
    setMonthError(false);
  };

  const handleShiftChange = (_, newValue) => {
    setSelectedShifts(newValue);
    setShiftError(false);
  };

  const validateFields = () => {
    let isValid = true;
    if (!selectedMonth) {
      setMonthError(true);
      isValid = false;
    }
    if (selectedShifts.length === 0) {
      setShiftError(true);
      isValid = false;
    }
    return isValid;
  };

  const handleClose = () => {
    setSelectedMonth(null);  // Reset month selection
    setSelectedShifts([]);   // Reset shifts selection
    setMonthError(false);    // Clear errors
    setShiftError(false);
    onClose();               // Call the parent close function
  };

  const handleCopy = async () => {
    if (!validateFields()) {
      toast.error("Please select both Month and Shifts before copying.");
      return;
    }

    const shiftMapping = [];
    shiftId.forEach(sourceId => {
      selectedShifts.forEach(shift => {
        shiftMapping.push({
          SourceShiftId: sourceId,
          DestShiftId: shift.id
        });
      });
    });

    const payload = {
      PropId: propId,
      YearNm: YearNm,
      MonthNm: MonthNm,
      ShiftMapping: shiftMapping,
      DestYear: dayjs().year(),
      DestMonth: selectedMonth?.name.split(" ")[0] || "",
      CreatedBy: userId,
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}HkTargetCalendar/CopyHkTargetCalendarToNewMonth`, payload, {
        headers: {
          Authorization: `Bearer ${Authorization}`,
          "Content-Type": "application/json",
        },
      });
      toast.success(response.data.Message);
      handleClose();
    } catch (error) {
      toast.error("Error copying data: " + (error.response?.data?.message || error.message));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: '#635bff',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2
      }}>
        <Box display="flex" alignItems="center">
          <ContentCopyIcon sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="600">Copy Data</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            From: {MonthNm} {YearNm}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CalendarMonthIcon sx={{ color: '#635bff', mr: 1 }} />
          <Typography variant="subtitle1" fontWeight="500">
            Select Destination Month
          </Typography>
        </Box>

        <Autocomplete
          options={monthData}
          getOptionLabel={(option) => option.name}
          value={selectedMonth}
          onChange={handleMonthChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Target Month"
              variant="outlined"
              fullWidth
              required
              error={monthError}
              helperText={monthError ? "Month is required" : ""}
            />
          )}
          sx={{ mb: 3 }}
          size="small"
        />

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <WorkIcon sx={{ color: '#635bff', mr: 1 }} />
          <Typography variant="subtitle1" fontWeight="500">
            Select Shifts to Copy
          </Typography>
        </Box>

        <Autocomplete
          multiple
          options={shiftData}
          disableCloseOnSelect
          getOptionLabel={(option) => option.name}
          value={selectedShifts}
          onChange={handleShiftChange}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                style={{ marginRight: 8 }}
                checked={selected}
                sx={{ color: '#635bff', '&.Mui-checked': { color: '#635bff' } }}
              />
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Target Shifts"
              variant="outlined"
              fullWidth
              required
              error={shiftError}
              helperText={shiftError ? "At least one shift must be selected" : ""}
            />
          )}
          sx={{ mb: 3 }}
          size="small"
        />

        {(selectedMonth || selectedShifts.length > 0) && (
          <Paper elevation={0} sx={{
            backgroundColor: "#f8f9fa",
            p: 2,
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Summary
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <Box component="span" fontWeight="500">Destination Month:</Box> {selectedMonth?.name || "None"}
            </Typography>
            <Typography variant="body2">
              <Box component="span" fontWeight="500">Selected Shifts:</Box> {selectedShifts.length ? selectedShifts.map(shift => shift.name).join(", ") : "None"}
            </Typography>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          sx={{
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        <Button
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
          variant="contained"
          sx={{
            backgroundColor: '#635bff',
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#524bd9'
            },
            boxShadow: '0 2px 6px rgba(99, 91, 255, 0.25)'
          }}
        >
          Copy Data
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyPopup;